import { unstable_batchedUpdates } from 'react-dom';
import { matchPath } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { history } from './history';
import { isUUID } from './lib/string';

const HOMEY_SCOPE_SEGMENTS = [
  'devices',
  'flows',
  'insights',
  'scripts',
  'logic',
  'settings',
  'test',
];

export const routingStore = createWithEqualityFn(
  (set, get, api) => ({
    homeyPath: '',
  }),
  shallow,
);

export class RouteManager {
  // If it should be routeable e.g. you are able to pass a link we add the dialog in the url. Else
  // we add it in the location state which is refreshable but not linkable.
  static dialogRoute = {
    deviceSettings: 'device-settings',
  };

  static dialogState = {
    saveAdvancedFlowDialog: 'saveAdvancedFlowDialog',
    newScriptDialog: 'newScriptDialog',
    newFlowDialog: 'newFlowDialog',
    commandPaletteDialog: 'commandPaletteDialog',
    timelineDialog: 'timelineDialog',
    appearanceDialog: 'appearanceDialog',
    userPresenceDialog: 'userPresenceDialog',
  };

  static matchPath = {
    script: `${import.meta.env.BASE_URL}homeys/:homeyId/scripts/:scriptId`,
    flow: `${import.meta.env.BASE_URL}homeys/:homeyId/flows/:flowId`,
    advancedFlow: `${import.meta.env.BASE_URL}homeys/:homeyId/flows/advanced/:advancedFlowId`,
    setting: `${import.meta.env.BASE_URL}homeys/:homeyId/settings/:settingSectionId/:settingId`,
  };

  static toHome(state) {
    const { homeyPath } = routingStore.getState();
    history.push(`${homeyPath}`, state);
  }

  static getPathForHome() {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}`;
  }

  static toHomey(homeyId, state) {
    history.push(RouteManager.getPathForHomey(homeyId), state);
  }

  static toFlow(flowId, state) {
    history.push(RouteManager.getPathForFlow(flowId), state);
  }

  static toAdvancedFlow(advancedFlowId, state) {
    history.push(RouteManager.getPathForAdvancedFlow(advancedFlowId), state);
  }

  static toNewFlow(state) {
    const { homeyPath } = routingStore.getState();
    const nextState = this.createStateForNewFlow(state);

    if (history.location.pathname.startsWith(`${homeyPath}/flows`)) {
      this.pushState(nextState);
      return;
    }

    this.toFlows(nextState);
  }

  static createStateForNewFlow(state) {
    return { [RouteManager.dialogState.newFlowDialog]: true, ...state };
  }

  static isActiveFlow(flowId) {
    return history.location.pathname.includes(flowId);
  }

  static toFlows(state) {
    const { homeyPath } = routingStore.getState();
    history.push(`${homeyPath}/flows`, state);
  }

  static toNewLogicVariable() {
    const { homeyPath } = routingStore.getState();

    if (history.location.pathname.startsWith(`${homeyPath}/flows`)) {
      this.pushState({ logicDialog: true });
      return;
    }

    this.toFlows({ logicDialog: true });
  }

  static toScript(scriptId, state) {
    history.push(RouteManager.getPathForScript(scriptId), state);
  }

  static toScripts(state) {
    history.push(RouteManager.getPathForScripts(), state);
  }

  static toNewScript() {
    const { homeyPath } = routingStore.getState();

    if (history.location.pathname.startsWith(`${homeyPath}/scripts`)) {
      this.pushState({ [RouteManager.dialogState.newScriptDialog]: true });
      return;
    }

    this.toScripts({ [RouteManager.dialogState.newScriptDialog]: true });
  }

  static toDevice(deviceId, state) {
    history.push(RouteManager.getPathForDevice(deviceId), state);
  }

  static toDevices(state) {
    const { homeyPath } = routingStore.getState();
    history.push(`${homeyPath}/devices`, state);
  }

  static getPathForDevices() {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/devices`;
  }

  static toNewDevice() {
    const { homeyPath } = routingStore.getState();

    if (
      history.location.pathname.startsWith(`${homeyPath}/devices`) ||
      history.location.pathname === homeyPath
    ) {
      this.pushState({
        pairDialog: {
          type: 'pair',
        },
      });
      return;
    }

    this.toDevices({
      pairDialog: {
        type: 'pair',
      },
    });
  }

  static toDeviceSettings(deviceId, state) {
    history.push(RouteManager.getPathForDeviceSettings(deviceId), state);
  }

  static getPathForDeviceSettings(deviceId) {
    const { homeyPath } = routingStore.getState();
    const isHomePath = history.location.pathname === homeyPath;
    const isDevicesPath = history.location.pathname.startsWith(`${homeyPath}/devices`);
    const pathname =
      isHomePath || isDevicesPath ? history.location.pathname : `${homeyPath}/devices`;
    const params = new URLSearchParams(
      isHomePath || isDevicesPath ? history.location.search : undefined,
    );

    params.set('dialog', RouteManager.dialogRoute.deviceSettings);
    params.set('key', deviceId);

    return `${pathname}?${params.toString()}`;
  }

  static toUserPresence(userId) {
    RouteManager.openDialog(RouteManager.dialogState.userPresenceDialog, userId);
  }

  static toFavoriteDevice(deviceId, state) {
    const { homeyPath } = routingStore.getState();
    history.push(`${homeyPath}/${deviceId}`, state);
  }

  static toInsights(state, search, { replace = false } = {}) {
    const { homeyPath } = routingStore.getState();

    if (replace === true) {
      history.replace(`${homeyPath}/insights${search != null ? `?${search}` : ''}`, state);
    } else {
      history.push(`${homeyPath}/insights${search != null ? `?${search}` : ''}`, state);
    }
  }

  static getPathForInsights(search) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/insights${search != null ? `?${search}` : ''}`;
  }

  static toCreatePat(state) {
    const { homeyPath } = routingStore.getState();
    history.push(`${homeyPath}/settings/system/api-keys/create`, state);
  }

  static toPat(patId, state) {
    const { homeyPath } = routingStore.getState();
    history.push(`${homeyPath}/settings/system/api-keys/${patId}`, state);
  }

  static toDeletePat(patId, state) {
    const { homeyPath } = routingStore.getState();
    history.push(`${homeyPath}/settings/system/api-keys/${patId}/delete`, state);
  }

  static toPremiumRequiredDialog() {
    this.pushState(this.getStateForPremiumRequiredDialog());
  }

  static toSetting(settingId, state) {
    history.push(RouteManager.getPathForSetting(settingId), state);
  }

  static toSettings(state) {
    history.push(RouteManager.getPathForSettings(), state);
  }

  static toPath(path, state) {
    history.push(path, state);
  }

  static getStateForPremiumRequiredDialog() {
    return { premiumRequiredDialog: true };
  }

  static getPathForHomey(homeyId) {
    const { homeyPath } = routingStore.getState();
    const nextHomeyPath = `${import.meta.env.BASE_URL}homeys/${homeyId}`;
    const currentPathname = history.location.pathname;

    if (typeof homeyPath !== 'string' || homeyPath === '') {
      return nextHomeyPath;
    }

    if (typeof currentPathname !== 'string' || currentPathname.startsWith(homeyPath) !== true) {
      return nextHomeyPath;
    }

    const relativePath = currentPathname.slice(homeyPath.length);
    const [scopeSegment] = relativePath.split('/').filter(Boolean);

    if (HOMEY_SCOPE_SEGMENTS.includes(scopeSegment) !== true) {
      return nextHomeyPath;
    }

    return `${nextHomeyPath}/${scopeSegment}`;
  }

  static getPathForDevice(deviceId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/devices/${deviceId}`;
  }

  static getPathForFlows() {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/flows`;
  }

  static getPathForFlow(flowId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/flows/${flowId}`;
  }

  static getPathForAdvancedFlow(advancedFlowId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/flows/advanced/${advancedFlowId}`;
  }

  static getPathForScript(scriptId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/scripts/${scriptId}`;
  }

  static getPathForScripts() {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/scripts/`;
  }

  static getPathForSetting(settingId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/settings/${settingId}`;
  }

  static getPathForSettings() {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/settings/`;
  }

  static getPathForDeleteUser(userId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/settings/system/family/${userId}/delete`;
  }

  static getPathForSwapOwner(userId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/settings/system/family/${userId}/swap-owner`;
  }

  static getPathForLocalUserEnable(userId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/settings/system/family/${userId}/local-user-enable`;
  }

  static getPathForLocalUserEdit(userId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/settings/system/family/${userId}/local-user-edit`;
  }

  static getPathForLocalUserUnsupported(userId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/settings/system/family/${userId}/local-user-unsupported`;
  }

  static getPathForLocalUserDisable(userId) {
    const { homeyPath } = routingStore.getState();
    return `${homeyPath}/settings/system/family/${userId}/local-user-disable`;
  }

  static pushState(state) {
    history.push(`${history.location.pathname}${history.location.search}`, state);
  }

  static extendState(state) {
    // TODO
    // implement
    // history.push(`${location.pathname}${location.search}`, state);
  }

  // Dialogs are kept in the history state instead of the query, so a refresh keeps them open while
  // the URL, and the unsaved-changes prompts that compare it, stay the same. Opening from another
  // dialog replaces its entry, so closing always goes back to the page without a dialog.
  static openDialog(name, value = true) {
    const hasOpenDialog = Object.values(RouteManager.dialogState).some((dialogName) => {
      return RouteManager.getDialogValue(history.location.state, dialogName) != null;
    });
    const path = `${history.location.pathname}${history.location.search}`;
    const state = { [name]: value };

    if (hasOpenDialog) {
      history.replace(path, state);
      return;
    }

    history.push(path, state);
  }

  // Goes back to the entry before the dialog, so Back and closing do the same. Does nothing when
  // the dialog's entry is already gone, e.g. when a command palette command navigated elsewhere.
  static closeDialog(name) {
    if (RouteManager.getDialogValue(history.location.state, name) == null) {
      return;
    }

    history.goBack();
  }

  /**
   * @param {unknown} locationState
   * @param {string} name
   * @returns {any} The value the dialog was opened with, or null when it isn't open.
   */
  static getDialogValue(locationState, name) {
    return locationState?.[name] ?? null;
  }

  static replaceState(state) {
    history.replace(`${history.location.pathname}${history.location.search}`, state);
  }

  static toOmitQuery() {
    history.push(`${history.location.pathname}`);
  }

  static getPathMatch(path) {
    return matchPath(history.location.pathname, path);
  }
}

export function useHomeyPath() {
  return routingStore((state) => state.homeyPath);
}

function listener(location, action) {
  const match = matchPath(location.pathname, `${import.meta.env.BASE_URL}homeys/:homeyId`);
  let homeyPath = match?.url ?? '';

  if (homeyPath.endsWith('/')) {
    homeyPath = homeyPath.substring(0, homeyPath.length - 1);
  }

  unstable_batchedUpdates(() => {
    routingStore.setState({ homeyPath });
  });
}

history.listen(listener);
listener(history.location);

/**
 *
 * @param match
 * @param location
 * @returns {boolean|*}
 */
export function getIsHomeActive(match, location) {
  if (match == null && location.pathname != null) {
    // For when the device controls are open on the homepage.
    let deviceId = null;

    if (import.meta.env.MODE === 'self-hosted') {
      [, , , , deviceId] = location.pathname.split('/');
    } else {
      [, , , deviceId] = location.pathname.split('/');
    }

    return isUUID(deviceId);
  }

  return true;
}
